import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Flex,
  Text,
} from "@chakra-ui/react";
import { Plus, Ruler, EllipsisVertical } from "lucide-react";
import Skeleton from "react-loading-skeleton";
import 'react-loading-skeleton/dist/skeleton.css';
import GradientButton from "components/Buttons/GradientButton";
import ReusableTable from "components/Tables/ReusableTable";
import ThreeDotMenu from "components/Menus/ThreeDotMenu";
import AddSizeEstimatesDrawer from "./components/AddSizeEstimatesDrawer";
import ViewSizeEstimates from "./components/ViewSizeEstimates";
import { fetchAllSizeEstimatesAction, addSizeEstimateAction } from "state/actions/packageActions";

const SizeEstimates = () => {
  const dispatch = useDispatch();
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [isAddDrawerOpen, setAddDrawerOpen] = useState(false);
  const [isViewDrawerOpen, setViewDrawerOpen] = useState(false);

  const sizeEstimates = useSelector((state) => state.packages.allSizeEstimates || []);
  const loading = useSelector((state) => state.packages.loading);

  useEffect(() => {
    dispatch(fetchAllSizeEstimatesAction());
  }, [dispatch]);

  const openAddDrawer = () => setAddDrawerOpen(true);
  const closeAddDrawer = () => setAddDrawerOpen(false);

  const openViewDrawer = (estimate) => {
    setSelectedEstimate(estimate);
    setViewDrawerOpen(true);
  };
  const closeViewDrawer = () => setViewDrawerOpen(false);

  // Add this handler function
  const handleAddSizeEstimate = (sizeEstimateData) => {
    dispatch(addSizeEstimateAction(sizeEstimateData))
      .then((result) => {
        if (result.success) {
          // Refresh the list after successful addition
          dispatch(fetchAllSizeEstimatesAction());
          closeAddDrawer();
        } else {
          // Handle error (you might want to show a toast notification)
          console.error("Failed to add size estimate:", result.error);
        }
      })
      .catch((error) => {
        console.error("Error adding size estimate:", error);
      });
  };

  const columns = [
    {
      Header: "Name",
      accessor: "size_category",
    },
    {
      Header: "Dimensions (cm)",
      accessor: "dimensions",
      Cell: ({ row }) => {
        const { length, width, height } = row.original;
        return `${length}×${width}×${height}`;
      },
    },
    {
      Header: "Weight (kg)",
      accessor: "weight",
      Cell: ({ row }) => `${row.original.weight}`,
    },
    {
      Header: "Created At",
      accessor: "created_at",
      Cell: ({ row }) => new Date(row.original.created_at).toLocaleString(),
    },
    {
      Header: "Updated At",
      accessor: "updated_at",
      Cell: ({ row }) => new Date(row.original.updated_at).toLocaleString(),
    },
    {
      Header: "Actions",
      accessor: "actions",
      Cell: ({ row }) => (
        <ThreeDotMenu
          icon={<EllipsisVertical size={20} color="grey" />}
          items={[
            {
              icon: <Ruler size={16} />,
              text: "View / Edit",
              onClick: () => openViewDrawer(row.original),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Flex direction="column" pt={{ base: "120px", md: "75px" }}>
      <Box p={6}>
        <Flex justifyContent="space-between" alignItems="center" mb={4}>
          {loading ? (
            <Skeleton height="24px" width="150px" />
          ) : (
            <Text fontSize="lg" fontWeight="bold">
              Size Estimates ({sizeEstimates.length})
            </Text>
          )}

          {loading ? (
            <Skeleton height="40px" width="140px" borderRadius="md" />
          ) : (
            <GradientButton
              icon={<Plus size={16} />}
              onClick={openAddDrawer}
              height="40px"
            >
              Add Size Estimate
            </GradientButton>
          )}
        </Flex>
      </Box>

      <Box p={6}>
        <ReusableTable
          columnsData={columns}
          tableData={sizeEstimates}
          initialState={{
            pageSize: 10,
            sortBy: [{ id: "name", desc: false }],
          }}
          isLoading={loading}
        />
      </Box>

      {/* Add Drawer - Now with onAddSizeEstimate prop */}
      <AddSizeEstimatesDrawer
        isOpen={isAddDrawerOpen}
        onClose={closeAddDrawer}
        onAddSizeEstimate={handleAddSizeEstimate}
      />

      {/* View/Edit Drawer */}
      <ViewSizeEstimates
        isOpen={isViewDrawerOpen}
        onClose={closeViewDrawer}
        estimate={selectedEstimate}
      />
    </Flex>
  );
};

export default SizeEstimates;